import { ReactNode } from 'react';

import type { PasswordInputProps as MantineInputProps } from '@mantine/core';
import { Box, Center, PasswordInput as MantinePasswordInput, Stack, Text } from '@mantine/core';
import { IconX } from '@tabler/icons-react';
import { IconCheck } from 'ui-icons';

import { TransitionElement } from '../Miscellaneous';

export interface PasswordInputProps extends MantineInputProps {
  confirmation?: boolean;
  password_confirmation_props: Partial<
    Omit<PasswordInputProps, 'confirmation' & 'password_confirmation_props'>
  >;
}

const PasswordInput = MantinePasswordInput;

const PassowrdInputDefaultProps = {
  radius: 'sm',
};

const PassowrdInputDefaults = PasswordInput.extend({
  defaultProps: PassowrdInputDefaultProps,
});
export { PassowrdInputDefaults, PasswordInput };

function PasswordRequirement({
  meets,
  label,
  error,
}: {
  meets: boolean;
  label: string;
  error?: ReactNode;
}) {
  return (
    <Text c={meets ? 'mint' : error ? 'cerise.7' : 'slate.2'} fz={10} fw={500}>
      <Center inline>
        {meets ? <IconCheck size={12} stroke={2} /> : <IconX size={12} stroke={2} />}
        <Box ml={8}>{label}</Box>
      </Center>
    </Text>
  );
}

const password_requirements = [
  { re: /^.{8,}$/, label: 'Have at least 8 characters' },
  { re: /[0-9]/, label: 'Includes at least 1 number' },
  { re: /[a-z]/, label: 'Includes at least 1 lowercase letter' },
  { re: /[A-Z]/, label: 'Includes at least 1 uppercase letter' },
  { re: /[$&+,:;=?@#|'<>.^*()%!-]/, label: 'Includes at least 1 special character' },
];
//const password_match = {re: /^a$/, label: 'Passwords must match!'}

export const validatePassword = (value: string) => {
  const state = password_requirements.some((requirement) => {
    if (!requirement.re.test(value)) {
      return requirement.label;
    }
  });

  return state;
};

export function PasswordWithStrength(props: Partial<PasswordInputProps>) {
  const valueAsString = props?.value ? String(props.value) : '';

  const checks = password_requirements.map((requirement, index) => (
    <PasswordRequirement
      key={index}
      label={requirement.label}
      meets={requirement.re.test(valueAsString)}
      error={props.error}
    />
  ));

  return (
    <Stack>
      <PasswordInput placeholder="Your password" required defaultVisible={false} {...props} />
      {props.confirmation && (
        <PasswordInput
          placeholder="Re-enter password"
          required
          defaultVisible={false}
          {...props.password_confirmation_props}
        />
      )}
      <Box
        style={{
          transition: 'all .25s ease',
          maxHeight: props.value ? '100px' : '0px',
          minHeight: props.value ? '50px' : '0px',
          marginBottom: '16px',
        }}
      >
        <TransitionElement mounted={!!props.value}>
          <Box mb="sm">{checks}</Box>
        </TransitionElement>
      </Box>
    </Stack>
  );
}
